import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createIntentRequestSchema } from "@/validation/create-intent-request";
import { zodToFieldErrors, firstFieldErrors } from "@trillion/validation";
import { Button } from "@trillion/ui/components/ui/button";
import { Input } from "@trillion/ui/components/ui/input";
import { Label } from "@trillion/ui/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@trillion/ui/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@trillion/ui/components/ui/card";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { TrafficIntent } from "@/types";

const SOURCE_TYPES = ["domain", "referrer", "utm", "direct", "social"] as const;
const BUYING_STAGES = ["discovery", "research", "comparison", "purchase", "support"] as const;
const CHANNELS = ["organic_search", "paid_search", "paid_social", "organic_social", "direct", "referral"] as const;

interface IntentFormProps {
  intent?: TrafficIntent;
  onSubmit: (data: IntentFormData) => Promise<void>;
  submitLabel: string;
}

export interface IntentFormData {
  sourceType: string;
  sourceKey: string;
  topic: string;
  category: string;
  buyingStage: string;
  channel: string;
  confidence: number;
  greeting: string;
  suggestionChips: string[];
  geoRelevance: string;
}

export function IntentForm({ intent, onSubmit, submitLabel }: IntentFormProps) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [sourceType, setSourceType] = useState<string>(intent?.source_type ?? "domain");
  const [sourceKey, setSourceKey] = useState(intent?.source_key ?? "");
  const [topic, setTopic] = useState(intent?.topic ?? "");
  const [category, setCategory] = useState(intent?.category ?? "");
  const [buyingStage, setBuyingStage] = useState<string>(intent?.buying_stage ?? "discovery");
  const [channel, setChannel] = useState<string>(intent?.channel ?? "");
  const [confidence, setConfidence] = useState(intent?.confidence ?? 0.5);
  const [greeting, setGreeting] = useState(intent?.greeting ?? "");
  const [suggestionChips, setSuggestionChips] = useState(
    (intent?.suggestion_chips ?? []).join(", ")
  );
  const [geoRelevance, setGeoRelevance] = useState(intent?.geo_relevance ?? "");

  const [errors, setErrors] = useState<Record<string, string>>({});

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    let chips: string[] | undefined;
    if (suggestionChips.trim()) {
      chips = suggestionChips.split(",").map((s) => s.trim()).filter(Boolean);
    }

    const payload = {
      source_type: sourceType,
      source_key: sourceKey.trim(),
      topic: topic.trim(),
      category: category.trim() || undefined,
      buying_stage: buyingStage,
      channel: channel || undefined,
      confidence,
      greeting: greeting.trim() || undefined,
      suggestion_chips: chips,
      geo_relevance: geoRelevance.trim() || undefined,
    };

    const result = createIntentRequestSchema.safeParse(payload);
    if (!result.success) {
      // Map snake_case field names to camelCase for the error display
      const snakeErrors = firstFieldErrors(zodToFieldErrors(result.error));
      const mapped: Record<string, string> = {};
      for (const [key, msg] of Object.entries(snakeErrors)) {
        const camelKey = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
        mapped[camelKey] = msg;
      }
      setErrors(mapped);
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        sourceType,
        sourceKey: sourceKey.trim(),
        topic: topic.trim(),
        category: category.trim(),
        buyingStage,
        channel,
        confidence,
        suggestionChips: chips ?? [],
        greeting: greeting.trim(),
        geoRelevance: geoRelevance.trim(),
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Operation failed");
    } finally {
      setIsSubmitting(false);
    }
  }

  function formatChannelLabel(ch: string): string {
    return ch
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Intent Details</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div className="grid gap-5 md:grid-cols-2">
            {/* Source Type */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="sourceType">Source Type *</Label>
              <Select
                value={sourceType}
                onValueChange={setSourceType}
                disabled={!!intent}
              >
                <SelectTrigger id="sourceType">
                  <SelectValue placeholder="Select source type" />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_TYPES.map((st) => (
                    <SelectItem key={st} value={st}>
                      {st.charAt(0).toUpperCase() + st.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.sourceType && (
                <p className="text-xs text-destructive">{errors.sourceType}</p>
              )}
            </div>

            {/* Source Key */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="sourceKey">Source Key *</Label>
              <Input
                id="sourceKey"
                value={sourceKey}
                onChange={(e) => setSourceKey(e.target.value)}
                placeholder="e.g., example.com or campaign_name"
                disabled={!!intent}
              />
              {errors.sourceKey && (
                <p className="text-xs text-destructive">{errors.sourceKey}</p>
              )}
            </div>

            {/* Topic */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="topic">Topic *</Label>
              <Input
                id="topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g., home insurance"
              />
              {errors.topic && (
                <p className="text-xs text-destructive">{errors.topic}</p>
              )}
            </div>

            {/* Category */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g., insurance"
              />
            </div>

            {/* Buying Stage */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="buyingStage">Buying Stage *</Label>
              <Select value={buyingStage} onValueChange={setBuyingStage}>
                <SelectTrigger id="buyingStage">
                  <SelectValue placeholder="Select buying stage" />
                </SelectTrigger>
                <SelectContent>
                  {BUYING_STAGES.map((stage) => (
                    <SelectItem key={stage} value={stage}>
                      {stage.charAt(0).toUpperCase() + stage.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.buyingStage && (
                <p className="text-xs text-destructive">{errors.buyingStage}</p>
              )}
            </div>

            {/* Channel */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="channel">Channel</Label>
              <Select value={channel || "_none"} onValueChange={(val) => setChannel(val === "_none" ? "" : val)}>
                <SelectTrigger id="channel">
                  <SelectValue placeholder="Select channel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">None</SelectItem>
                  {CHANNELS.map((ch) => (
                    <SelectItem key={ch} value={ch}>
                      {formatChannelLabel(ch)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Confidence */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="confidence">Confidence (0-1)</Label>
              <Input
                id="confidence"
                type="number"
                min={0}
                max={1}
                step={0.1}
                value={confidence}
                onChange={(e) => setConfidence(parseFloat(e.target.value) || 0)}
              />
              {errors.confidence && (
                <p className="text-xs text-destructive">{errors.confidence}</p>
              )}
            </div>

            {/* Geo Relevance */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="geoRelevance">Geo Relevance</Label>
              <Input
                id="geoRelevance"
                value={geoRelevance}
                onChange={(e) => setGeoRelevance(e.target.value)}
                placeholder="e.g., US, UK"
              />
            </div>
          </div>

          {/* Greeting (full width) */}
          <div className="flex flex-col gap-2">
            <Label htmlFor="greeting">Greeting</Label>
            <textarea
              id="greeting"
              value={greeting}
              onChange={(e) => setGreeting(e.target.value)}
              placeholder="Welcome message for this intent"
              rows={3}
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          {/* Suggestion Chips (full width) */}
          <div className="flex flex-col gap-2">
            <Label htmlFor="suggestionChips">Suggestion Chips</Label>
            <Input
              id="suggestionChips"
              value={suggestionChips}
              onChange={(e) => setSuggestionChips(e.target.value)}
              placeholder="Comma-separated, e.g., Get a quote, Compare plans, Learn more"
            />
            <p className="text-xs text-muted-foreground">
              Enter chips separated by commas. They will be stored as a JSON array.
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="size-4 animate-spin" />}
              {submitLabel}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/intents")}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
